import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "@mui/material";

/**
 * Debounced keyword filter synced to the URL query string. General-purpose:
 * usable on any page with a search box. Uses history.replace so rapid typing
 * doesn't spam the browser back stack.
 *
 * @param {Object} [options]
 * @param {string} [options.paramName="keyword"] - URL/query param name.
 * @param {number} [options.delay=500] - debounce delay in ms.
 * @param {string[]} [options.resetParams=[]] - extra params to clear on a new
 *   search (e.g. pass useServerPagination().resetParams on list pages to return
 *   to the first page). Must be referentially stable.
 * @returns {{
 *   keyword: string,
 *   searchProps: Object, // spread into <SearchTextField />
 * }}
 */
export const useSearchFilter = ({
  paramName = "keyword",
  delay = 500,
  resetParams = [],
} = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get(paramName) ?? "";

  const setKeyword = useCallback(
    (value) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (value) params.set(paramName, value);
          else params.delete(paramName);
          resetParams.forEach((param) => params.delete(param));
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams, paramName, resetParams],
  );

  // Memoized so the debounce timer survives re-renders (recreating it per
  // render would drop pending updates). setKeyword is stable through a typing
  // burst since the URL only changes once the debounce fires.
  const debouncedSetKeyword = useMemo(
    () => debounce(setKeyword, delay),
    [setKeyword, delay],
  );

  useEffect(() => () => debouncedSetKeyword.clear(), [debouncedSetKeyword]);

  const searchProps = {
    defaultValue: keyword,
    onChange: (event) => debouncedSetKeyword(event.target.value),
  };

  return { keyword, searchProps };
};
